// Bulk-attach real documents to EXISTING certificates. Pick many files at once;
// each is auto-matched to a certificate by filename (vs title / number) and you
// confirm the target, then every file is stored + attached immediately (original
// filename kept). No AI, no 4-file cap.
import { useMemo, useState } from "react";
import { X, Loader2, FileText, Paperclip, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "var(--brand-accent, #C75A1B)";
const selCls = "w-full bg-white border rounded-[7px] px-2.5 py-1.5 text-[12px] text-[#1F1F1F] outline-none focus:border-[var(--brand-accent,#C75A1B)]";

const norm = (s) => (s || "").toLowerCase().replace(/\.[a-z0-9]+$/, "").replace(/[^a-z0-9]+/g, "");

function autoMatch(fileName, certs, usedIds) {
    const fn = norm(fileName);
    if (!fn) return "";
    // Prefer certs without a file, then any cert.
    const pools = [certs.filter((c) => !c.file?.storage_path), certs];
    for (const pool of pools) {
        for (const c of pool) {
            if (usedIds.has(c.id)) continue;
            const t = norm(c.title);
            const num = norm(c.certificate_number);
            if ((t && (fn.includes(t) || t.includes(fn))) || (num && num.length >= 3 && fn.includes(num))) {
                return c.id;
            }
        }
    }
    return "";
}

export default function CertBulkAttach({ files, clientId, items, onClose, onSaved }) {
    const [rows, setRows] = useState(() => {
        const used = new Set();
        return files.map((f, i) => {
            const target = autoMatch(f.name, items, used);
            if (target) used.add(target);
            return { key: `${i}-${f.name}`, fileObj: f, name: f.name, targetId: target, status: "pending", error: null };
        });
    });
    const [saving, setSaving] = useState(false);

    const setTarget = (idx, id) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, targetId: id } : r)));
    const removeRow = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));

    const matchedCount = useMemo(() => rows.filter((r) => r.targetId).length, [rows]);

    async function attachAll() {
        const toDo = rows.filter((r) => r.targetId && r.status !== "done");
        if (!toDo.length) { toast.error("Match at least one file to a certificate."); return; }
        setSaving(true);
        for (const r of toDo) {
            const idx = rows.indexOf(r);
            setRows((rs) => rs.map((x, i) => (i === idx ? { ...x, status: "uploading", error: null } : x)));
            try {
                const fd = new FormData();
                fd.append("file", r.fileObj);
                await api.post(`/certificates/${r.targetId}/attach`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                setRows((rs) => rs.map((x, i) => (i === idx ? { ...x, status: "done" } : x)));
            } catch (e) {
                setRows((rs) => rs.map((x, i) => (i === idx ? { ...x, status: "error", error: formatApiError(e?.response?.data?.detail) || "Failed" } : x)));
            }
        }
        setSaving(false);
        const done = rows.filter((r) => r.status === "done" || (r.targetId && r.status !== "error")).length;
        toast.success(`Attached ${toDo.filter((r) => rows[rows.indexOf(r)]?.status !== "error").length || done} file(s)`);
        onSaved();
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
            <div data-testid="cert-bulk-attach-dialog" onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-[14px] shadow-brand w-full max-w-[760px] max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-3.5 border-b sticky top-0 bg-white z-10" style={{ borderColor: "#F0EDE8" }}>
                    <div className="font-semibold text-[#1F1F1F] flex items-center gap-2">
                        <Paperclip className="w-4 h-4" style={{ color: ACCENT }} /> Attach files to certificates · {rows.length} file{rows.length !== 1 ? "s" : ""}
                    </div>
                    <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-[7px] hover:bg-[#F6F4F0]"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-4 space-y-2.5">
                    <p className="text-[12px]" style={{ color: "#6F6A64" }}>
                        Each file is matched to a certificate by its filename — confirm or change the target, then attach. The original filename is kept.
                    </p>
                    {rows.map((r, idx) => (
                        <div key={r.key} data-testid={`cert-attach-row-${idx}`} className="border rounded-[10px] p-2.5 flex items-center gap-2.5" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                            <FileText className="w-4 h-4 shrink-0" style={{ color: "#6F6A64" }} />
                            <span className="text-[12px] font-medium text-[#1F1F1F] truncate flex-1 min-w-0" title={r.name}>{r.name}</span>
                            <span className="text-[#9A968F] text-[11px]">→</span>
                            <select data-testid={`cert-attach-target-${idx}`} value={r.targetId} onChange={(e) => setTarget(idx, e.target.value)} className={selCls + " max-w-[280px]"}>
                                <option value="">— Skip —</option>
                                {items.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.title}{c.category === "company" ? " (Company)" : ""}{c.file?.storage_path ? " · has file" : ""}
                                    </option>
                                ))}
                            </select>
                            {r.status === "uploading" && <Loader2 className="w-4 h-4 animate-spin shrink-0" style={{ color: ACCENT }} />}
                            {r.status === "done" && <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "#3F7D3A" }} />}
                            {r.status === "error" && <span title={r.error}><AlertTriangle className="w-4 h-4 shrink-0" style={{ color: "#B33A2E" }} /></span>}
                            <button onClick={() => removeRow(idx)} className="w-6 h-6 grid place-items-center rounded-[6px] hover:bg-[#FBE8E5] shrink-0" title="Remove"><X className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} /></button>
                        </div>
                    ))}
                    {rows.length === 0 && <div className="py-8 text-center italic text-[12px]" style={{ color: "#9A968F" }}>No files selected.</div>}
                </div>

                <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t sticky bottom-0 bg-white" style={{ borderColor: "#F0EDE8" }}>
                    <span className="text-[11.5px]" style={{ color: "#6F6A64" }}>{matchedCount} matched to a certificate</span>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>Close</button>
                        <button data-testid="cert-attach-save-button" onClick={attachAll} disabled={saving || !matchedCount}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white disabled:opacity-60" style={{ background: ACCENT }}>
                            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Attach {matchedCount} file{matchedCount !== 1 ? "s" : ""}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
